/**
 * YesNoSwitch — the renderer for FIELD_META type 'yesno' (tri-state
 * null | true | false). Two segments, Yes / No, with an UNSELECTED state —
 * unlike a checkbox, "No" is a recorded answer, not the absence of one.
 *
 * The ONE yes/no control: the wizard's matrix fields and the case file's Edit
 * Incident Details modal both render it. Legacy 'true' / 'false' strings
 * (values stored before the boolean shape) are read as their booleans, so a
 * caller can hand the raw type_specific value straight in; onChange always
 * emits a real boolean or null.
 *
 * Accessibility: a radiogroup of two radios. Tab lands on the selected segment
 * (or "Yes" when unanswered); Left/Right/Up/Down move selection; Space/Enter
 * select; Backspace/Delete on the selected segment clears; clicking the
 * selected segment again clears back to unanswered. Name the group with
 * `ariaLabelledby` (the id of a visible <label>) or `ariaLabel` — a <label
 * htmlFor> can't target the group because a div is not labelable.
 *
 * Styling: the shared `.yesno-switch` / `.yesno-opt(.active)` / `.yesno-hint`
 * classes in global.css.
 *
 * Props: { id?, value: null|true|false|'true'|'false', onChange(next), disabled?,
 *          ariaLabel?, ariaLabelledby?, yesLabel?, noLabel?,
 *          showState? (default true — "Not answered" hint) }
 */

import React, { useRef } from 'react';

const SEGMENTS = [
  { val: true,  key: 'yes' },
  { val: false, key: 'no'  },
];

/** null | true | false from a raw stored value (booleans or legacy strings). */
export function coerceYesNo(value) {
  if (value === true  || value === 'true')  return true;
  if (value === false || value === 'false') return false;
  return null;
}

export default function YesNoSwitch({
  id,
  value = null,
  onChange,
  disabled = false,
  ariaLabel,
  ariaLabelledby,
  yesLabel = 'Yes',
  noLabel  = 'No',
  showState = true,
}) {
  const refs = useRef({});
  const current = coerceYesNo(value);

  function select(next) {
    if (disabled) return;
    onChange?.(next);
  }

  function onKeyDown(e, val) {
    const other = !val;
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      select(other);
      refs.current[String(other)]?.focus();
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      if (current === val) { e.preventDefault(); select(null); }
    }
  }

  return (
    <div
      id={id}
      className="yesno-switch"
      role="radiogroup"
      aria-label={ariaLabelledby ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledby}
      data-state={current === null ? 'unset' : current ? 'yes' : 'no'}
    >
      {SEGMENTS.map(({ val, key }, i) => {
        const active  = current === val;
        // Roving tabindex: one tab stop — the selected segment, or "Yes" when unanswered.
        const tabStop = current == null ? i === 0 : active;
        return (
          <button
            key={key}
            ref={el => { refs.current[String(val)] = el; }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={tabStop ? 0 : -1}
            disabled={disabled}
            className={`yesno-opt${active ? ' active' : ''}`}
            title={active ? 'Click again to clear' : undefined}
            onClick={() => select(active ? null : val)}
            onKeyDown={e => onKeyDown(e, val)}
          >
            {val ? yesLabel : noLabel}
          </button>
        );
      })}
      {showState && current == null && (
        <span className="yesno-hint">Not answered</span>
      )}
    </div>
  );
}
